/**
 * MyOutreachActivities
 *
 * List view of the current user's outreach activity entries. The backend
 * (app/routers/outreach_activities.py::list_outreach_activities) applies
 * Promotora row-level scoping automatically: a staff/viewer-tier caller
 * sees only their own rows; a supervisor+ sees every row in the CBO.
 *
 * Used by Promotoras to review what they've logged and by Coordinators
 * / Managers to monitor the team's outreach.
 */

import React, { useEffect, useState } from 'react';
import { Plus, Filter, ClipboardList } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  listOutreachActivities,
  OutreachActivity,
} from '@/services/api/outreachActivities';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { useTabbedChrome } from '@/components/weavein';

interface MyOutreachActivitiesProps {
  /** When set, shows a "Log new activity" button that navigates the caller. */
  onCreateClick?: () => void;
}

export const MyOutreachActivities: React.FC<MyOutreachActivitiesProps> = ({ onCreateClick }) => {
  const { t } = useAppTranslation();
  const tabbed = useTabbedChrome();
  const [rows, setRows] = useState<OutreachActivity[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');

  useEffect(() => {
    let cancelled = false;
    setRows(null);
    setError(null);
    listOutreachActivities({
      date_from: dateFrom || undefined,
      date_to: dateTo || undefined,
      status: statusFilter || undefined,
      limit: 100,
    })
      .then((data) => {
        if (!cancelled) setRows(data);
      })
      .catch((e: any) => {
        if (!cancelled) setError(e?.message || t('outreach.load_failed', 'Failed to load activities.'));
      });
    return () => {
      cancelled = true;
    };
  }, [dateFrom, dateTo, statusFilter]);

  return (
    <div className="max-w-6xl mx-auto p-4 space-y-4">
      <header className="flex items-center justify-between gap-2">
        <div>
          {!tabbed && (
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <ClipboardList className="w-6 h-6 text-blue-600" />
            {t('outreach.title', 'Outreach Activities')}
          </h1>
          )}
          <p className="text-sm text-gray-500 mt-1">
            {t(
              'outreach.subtitle',
              "Aggregate counts you've logged. Supervisors see all entries in the organization.",
            )}
          </p>
        </div>
        {onCreateClick && (
          <Button onClick={onCreateClick}>
            <Plus className="w-4 h-4 mr-2" />
            {t('outreach.log_new', 'Log new activity')}
          </Button>
        )}
      </header>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <Filter className="w-4 h-4" />
            {t('common.filters', 'Filters')}
          </CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <label className="text-xs text-gray-500 block mb-1">{t('common.from', 'From')}</label>
            <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
          </div>
          <div>
            <label className="text-xs text-gray-500 block mb-1">{t('common.to', 'To')}</label>
            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
          </div>
          <div>
            <label className="text-xs text-gray-500 block mb-1">{t('common.status', 'Status')}</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="block w-full border border-gray-300 dark:border-gray-700 dark:bg-gray-900 rounded-md px-3 py-2 text-sm"
            >
              <option value="">{t('common.all', 'All')}</option>
              <option value="draft">{t('form_status.draft', 'Draft')}</option>
              <option value="submitted">{t('form_status.submitted', 'Submitted')}</option>
              <option value="approved">{t('form_status.approved', 'Approved')}</option>
              <option value="rejected">{t('form_status.rejected', 'Rejected')}</option>
            </select>
          </div>
        </CardContent>
      </Card>

      {error && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-4 text-sm text-red-800">{error}</CardContent>
        </Card>
      )}

      {rows === null && !error ? (
        <div className="text-sm text-gray-500 py-8 text-center">{t('common.loading', 'Loading…')}</div>
      ) : rows && rows.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-sm text-gray-500">
            {t('outreach.empty', 'No activities yet.')}{' '}
            {onCreateClick && (
              <button onClick={onCreateClick} className="text-blue-600 underline ml-1">
                {t('outreach.empty_cta', 'Log your first one.')}
              </button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {rows!.map((row) => (
            <ActivityRow key={row.id} row={row} />
          ))}
        </div>
      )}
    </div>
  );
};

const ActivityRow: React.FC<{ row: OutreachActivity }> = ({ row }) => {
  const { t } = useAppTranslation();
  const fd = row.field_data || {};
  const date = fd.outreach_activity_date || row.created_ts?.slice(0, 10);
  const activityType = fd.outreach_activity_type;
  const engagementType = fd.outreach_engagement_type;
  const reach = fd.outreach_num_immigrants_refugees_reached;
  const languages = Array.isArray(fd.outreach_languages_used)
    ? fd.outreach_languages_used.join(', ')
    : fd.outreach_languages_used;

  return (
    <Card>
      <CardContent className="p-3">
        <div className="flex justify-between items-start gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-medium">{activityType || t('outreach.untyped_activity', 'Outreach activity')}</span>
              {engagementType && (
                <Badge variant="outline" className="text-xs">
                  {engagementType}
                </Badge>
              )}
              <StatusBadge status={row.status} />
            </div>
            <div className="text-xs text-gray-500 mt-1 flex gap-3 flex-wrap">
              {date && <span>{date}</span>}
              {reach !== undefined && reach !== null && (
                <span>{t('outreach.people_reached', '· {{count}} reached', { count: reach })}</span>
              )}
              {languages && <span>· {languages}</span>}
              {fd.outreach_location_of_outreach && <span>· {fd.outreach_location_of_outreach}</span>}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const StatusBadge: React.FC<{ status: OutreachActivity['status'] }> = ({ status }) => {
  const { t } = useAppTranslation();
  const colors: Record<string, string> = {
    draft: 'bg-gray-100 text-gray-700',
    submitted: 'bg-blue-100 text-blue-700',
    approved: 'bg-green-100 text-green-700',
    rejected: 'bg-red-100 text-red-700',
  };
  return (
    <span className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded ${colors[status] || colors.draft}`}>
      {t(`form_status.${status}`, status)}
    </span>
  );
};

export default MyOutreachActivities;
